import { Component, inject } from '@angular/core';
import { PageComponent, Transfer, PagedArray, sleep } from '@tytapp/common';
import { ActivatedRoute, Params } from '@angular/router';
import { UserService } from '@tytapp/user';
import { ApiProduction, ApiShow, ApiUser, ProductionsApi, ShowsApi } from '@tytapp/api';
import { environment } from '@tytapp/environment';
import { isClientSide } from '@tytapp/environment-utils';

const DAY_INTERVAL = 1000 * 60 * 60 * 24;

interface Day {
    /**
     * If null, not part of the month
     */
    url?: string;
    nextDayUrl?: string;
    lastDayUrl?: string;
    date: Date;
    dayString?: string;
    episodes?: ApiProduction[];
    episode?: ApiProduction;
    shows: ApiShow[];
}

interface Request {
    mode: 'years' | 'year' | 'month' | 'date' | 'invalid';
    year?: number;
    month?: number;
    day?: number;
    date?: Date;
}

interface YearStat {
    year: number;
}

interface MonthStat {
    month: string;
}

@Component({
    selector: 'tyt-production-calendar',
    templateUrl: './production-calendar.component.html',
    styleUrls: ['./production-calendar.component.scss']
})
export class ProductionCalendarComponent extends PageComponent {
    private userService = inject(UserService);
    private productionsApi = inject(ProductionsApi);
    private showsApi = inject(ShowsApi);

    get pageComponentId() {
        return 'archive';
    }

    currentTask: Promise<void>;
    pastShows: ApiShow[];
    request: Request;

    faded: boolean = false;

    user: ApiUser;
    monthUrl: string;
    nextMonthUrl: string;
    lastMonthUrl: string;

    years: YearStat[];
    months: MonthStat[];
    days: Day[] = [];
    selectedDay: Day = null;

    ready: boolean;
    mode: 'episodes' | 'clips' | 'podcasts' = 'episodes';

    private shows: PagedArray<ApiShow>;
    private monthContent: Record<string, Record<string, PagedArray<ApiProduction>>> = {};

    private monthOfDate(date: Date) {
        return new Date(date.getFullYear(), date.getMonth(), 1);
    }

    digits(number: number, minDigits = 2) {
        let max = Math.pow(10, (minDigits - 1));
        let numStr = number.toString();
        if (number >= max)
            return numStr;
        let maxStr = max.toString();
        return maxStr.slice(1, maxStr.length - numStr.length + 1) + numStr;
    }

    monthStringFor(date: Date) {
        if (!date)
            return ``;
        return `${date.getFullYear()}-${this.digits(date.getMonth() + 1, 2)}`;
    }

    dayStringFor(date: Date) {
        if (!date)
            return ``;
        return `${date.getFullYear()}-${this.digits(date.getMonth() + 1, 2)}-${this.digits(date.getDate(), 2)}`;
    }

    nextMonthFor(date: Date) {
        return new Date(date.getFullYear(), date.getMonth() + 1, 1);
    }

    lastMonthFor(date: Date) {
        return new Date(date.getFullYear(), date.getMonth() - 1, 1);
    }

    private pad(n, width, z = '0') {
        n = n + '';
        return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
    }

    isValidRequest(request: Request) {
        let now = new Date();

        if (request.year) {
            if (request.year < 2011)
                return false;

            if (request.year > now.getFullYear())
                return false;

            if (request.year === now.getFullYear() && request.month > now.getMonth() + 1)
                return false;
        }

        if (request.date && isNaN(request.date.getTime()))
            return false;

        // Detect invalid dates (which parse as if they existed, ie 2024-02-30 is 2024-03-01)

        if (request.year && request.year !== request.date.getFullYear())
            return false;

        if (request.month && request.month !== request.date.getMonth() + 1)
            return false;

        if (request.day && request.day !== request.date.getDate())
            return false;

        return true;
    }

    async init() {
        this.pageTitle = `Archive by Date`;

        const enable_archive_search = await this.shell.hasFeature('apps.web.enable_archive_search');
        if (enable_archive_search) {
            return this.router.navigateByUrl('/archive/search');
        }

        this.populateYears();

        this.subscribe(this.userService.userChanged, user => {
            this.user = user;
        });

        this.route.params.subscribe(async params => {
            await this.currentTask;
            await (this.currentTask = this.load(params));
        });

        this.pastShows = (await this.showsApi.past().toPromise());
    }

    private populateYears() {
        let minYear = 2011;
        let maxYear = new Date().getFullYear();

        let years: YearStat[] = [];

        for (let year = minYear; year <= maxYear; ++year)
            years.push({ year });

        this.years = years;
    }

    private async load(params: Params) {
        await sleep();

        let request = this.parseDate(params.date);
        if (request.mode === 'invalid' || !this.isValidRequest(request)) {
            console.error(request);
            this.router.navigateByUrl('/archive');
            return;
        }

        this.request = request;

        if (params.day)
            this.head.setCanonicalLink(`/archive/${params.day}`);
        else
            this.head.setCanonicalLink(`/archive`);

        let dayToShow: string = null;
        let dateStr: string = params.date ?? 'years';

        let dateStrParts = dateStr.split('-');
        if (dateStrParts.length >= 3) {
            dayToShow = dateStrParts[2];
            dateStr = dateStr.replace(/(\d+)-(\d+).*/, '$1-$2');
        }

        if (request.year) {
            this.months = this.calculateMonths(request.year);
        }

        if (request.month) {
            await this.loadMonth(this.monthOfDate(request.date), request.mode === 'month');
        }

        if (request.day) {
            this.faded = true;
            this.selectedDay = this.findDate(request.date);
            this.selectedDay.episodes ??= await this.productionsApi.getArchivedByDate(params.date).toPromise();
            this.faded = false;
        }

        this.ready = true;
    }

    private calculateMonths(year: number) {
        let months: MonthStat[] = [];
        let now = new Date().getTime();

        for (var i = 1; i <= 12; ++i) {

            let startOfMonth = new Date(`${year}-${this.pad(i, 2)}-01`);
            if (startOfMonth.getTime() > now)
                break;

            months.push({ month: `${year}-${this.pad(i, 2)}` });
        }

        return months;
    }

    async loadMonth(month: Date, includeContent = false) {
        let monthStr = `${month.getFullYear()}-${this.digits(month.getMonth() + 1, 2)}`;

        let offset = month.getDay();
        this.days = [];

        this.monthUrl = `/archive/${monthStr}`;
        this.nextMonthUrl = `/archive/${this.monthStringFor(this.nextMonthFor(month))}`;
        this.lastMonthUrl = `/archive/${this.monthStringFor(this.lastMonthFor(month))}`;

        let showsByDate: Record<string, ApiProduction[]> = {};
        this.shows ??= await this.showsApi.all().toPromise() as any;

        if (includeContent) {
            showsByDate = this.monthContent[monthStr] ??= await this.productionsApi.getShowsInMonth(monthStr).toPromise();
        }

        for (let col = 0; col < 6; ++col) {
            for (let row = 0; row < 7; ++row) {
                let index = col * 7 + row;
                let date = new Date(month.getFullYear(), month.getMonth(), index - offset + 1);
                let monthOfDate = this.monthOfDate(date);

                if (index < offset) {
                    this.days.push({ date: null } as any);
                    continue;
                } else if (monthOfDate.getTime() != month.getTime()) {
                    break;
                }

                let dayDate = new Date(date.getTime() + date.getTimezoneOffset());
                let dayString = this.dayStringFor(dayDate);

                let previousDay = new Date(dayDate.getTime() - DAY_INTERVAL);
                let nextDay = new Date(dayDate.getTime() + DAY_INTERVAL);

                let day: Day = {
                    date: dayDate,
                    url: `${this.monthUrl}-${this.digits(date.getDate(), 2)}`,
                    lastDayUrl: `/archive/${this.dayStringFor(previousDay)}`,
                    nextDayUrl: `/archive/${this.dayStringFor(nextDay)}`,
                    dayString,
                    shows: (showsByDate[dayString] || []).map(id => this.shows.find(y => y.id == id)).filter(x => x),
                    episodes: null
                };

                this.days.push(day);
            }
        }
    }

    get isDeepArchive() {
        if (!this.selectedDay)
            return false;

        let deepArchiveEnds = new Date(`2018-01-01`);
        let deepArchiveStarts = new Date(`2011-01-01`);
        return (this.selectedDay.date.getTime() > deepArchiveStarts.getTime())
            && (this.selectedDay.date.getTime() < deepArchiveEnds.getTime());
    }

    get deepArchiveLink() {
        let defaultUrl = 'https://tytnetwork.com/annual-archives/#';

        if (!this.selectedDay)
            return defaultUrl;

        let dayString = this.selectedDay.dayString;
        let monthString = dayString.split(/-/g, 3)[1];
        let yearString = dayString.split(/-/g, 3)[0];

        let deepArchiveUrls = {
            '2012': 'https://tytnetwork.com/annual-archives-legacy/2012-main-show-archive/',
            '2013': 'https://tytnetwork.com/annual-archives-legacy/2013-main-show-archive/',
            '2014': 'https://tytnetwork.com/annual-archives-legacy/2014-main-show-archives/',
            '2015': 'https://tytnetwork.com/annual-archives-legacy/2015-archives/',
            '2016': {
                '01': 'https://tytnetwork.com/annual-archives/2016-archives/january-2016-media-archives/',
                '02': 'https://tytnetwork.com/annual-archives/2016-archives/february-2016-media-archives/',
                '03': 'https://tytnetwork.com/annual-archives/2016-archives/march-2016-media-archives/',
                '04': 'https://tytnetwork.com/annual-archives/2016-archives/april-2016-media-archives/',
                '05': 'https://tytnetwork.com/annual-archives/2016-archives/may-2016-media-archives/',
                '06': 'https://tytnetwork.com/annual-archives/2016-archives/june-2016-media-archives/',
                '07': 'https://tytnetwork.com/annual-archives/july-2016-media-archives/',
                '08': 'https://tytnetwork.com/annual-archives/august-2016-media-archives/',
                '09': 'https://tytnetwork.com/annual-archives/2016-archives/september-2016-media-archives/',
                '10': 'https://tytnetwork.com/annual-archives/2016-archives/october-2016-media-archives/',
                '11': 'https://tytnetwork.com/annual-archives/2016-archives/november-2016-media-archives/',
                '12': 'https://tytnetwork.com/annual-archives/2016-archives/december-2016-media-archives/',
            },
            '2017': {
                '01': 'https://tytnetwork.com/annual-archives/2017-archives/january-2017-media-archives/',
                '02': 'https://tytnetwork.com/annual-archives/2017-archives/february-2017-media-archives/',
                '03': 'https://tytnetwork.com/annual-archives/2017-archives/march-2017-media-archives/',
                '04': 'https://tytnetwork.com/monthly-archive-template/?archive_month=4&archive_year=2017',
                '05': 'https://tytnetwork.com/monthly-archive-template/?archive_month=5&archive_year=2017',
                '06': 'https://tytnetwork.com/monthly-archive-template/?archive_month=6&archive_year=2017',
                '07': 'https://tytnetwork.com/monthly-archive-template/?archive_month=7&archive_year=2017',
                '08': 'https://tytnetwork.com/monthly-archive-template/?archive_month=8&archive_year=2017',
                '09': 'https://tytnetwork.com/monthly-archive-template/?archive_month=9&archive_year=2017',
                '10': 'https://tytnetwork.com/monthly-archive-template/?archive_month=10&archive_year=2017',
                '11': 'https://tytnetwork.com/monthly-archive-template/?archive_month=11&archive_year=2017',
                '12': 'https://tytnetwork.com/monthly-archive-template/?archive_month=12&archive_year=2017',
            }
        };

        let yearArchives = deepArchiveUrls[yearString];

        if (!yearArchives)
            return defaultUrl;

        if (typeof yearArchives === 'string')
            return yearArchives;

        let monthArchives = yearArchives[monthString];

        return monthArchives || defaultUrl;
    }

    pushDayState(day: Day) {
        if (!day.date)
            return;

        if (isClientSide()) {
            window.history.pushState({ date: day.date }, null, `/archive/${this.monthStringFor(day.date)}/${this.digits(day.date.getDate(), 2)}`);
        }
    }

    findDate(date: Date): Day {
        let dayString = this.dayStringFor(date);
        let day = this.days.find(x => x.dayString && x.dayString == dayString);
        return day;
    }

    adjustDateForTimeZone(date: Date) {
        return new Date(date.getTime() + 1000 * 60 * date.getTimezoneOffset());
    }

    parseDate(dateStr: string): Request {
        if (!dateStr)
            return { mode: 'years' };

        const dayPattern = /^(\d+)-(\d+)-(\d+)$/;
        const monthPattern = /^(\d+)-(\d+)$/;
        const yearPattern = /^(\d+)$/;

        if (dayPattern.test(dateStr)) {
            let [_, year, month, day] = dayPattern.exec(dateStr);
            return {
                mode: 'date',
                year: Number(year),
                month: Number(month),
                day: Number(day),
                date: this.adjustDateForTimeZone(new Date(dateStr))
            };
        } else if (monthPattern.test(dateStr)) {
            let [_, year, month] = monthPattern.exec(dateStr);
            return {
                mode: 'month',
                year: Number(year),
                month: Number(month),
                date: this.adjustDateForTimeZone(new Date(dateStr))
            };
        } else if (yearPattern.test(dateStr)) {
            let [_, year] = yearPattern.exec(dateStr);
            return {
                mode: 'year',
                year: Number(year),
                date: this.adjustDateForTimeZone(new Date(dateStr))
            };
        } else {
            return { mode: 'invalid' };
        }
    }
}
